import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Bot, Pencil, Loader2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useAssistant } from '@/hooks/useAssistants'
import { useSubdomain } from '@/hooks/useSubdomain'
import { KnowledgeBasesTab } from './tabs/KnowledgeBasesTab'
import { SettingsTab } from './tabs/SettingsTab'
import { PromptTab } from './tabs/PromptTab'
import { TestTab } from './tabs/TestTab'
import { WidgetTab } from './tabs/WidgetTab'
import { IntegrationsTab } from './tabs/IntegrationsTab'
import { MemoryTab } from './tabs/MemoryTab'
import { EditAssistantModal } from '@/components/modals/EditAssistantModal'

export function AssistantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: assistant, isLoading, error } = useAssistant(id)
  const [showEditModal, setShowEditModal] = useState(false)
  const [activeTab, setActiveTab] = useState('knowledge-bases')
  const tenantSlug = useSubdomain() || ''

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error / 404 state
  if (error || !assistant) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Bot className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Assistant not found</h2>
        <p className="text-muted-foreground mb-6">
          The assistant you're looking for doesn't exist or has been deleted.
        </p>
        <Button onClick={() => navigate('/app/assistants')}>
          Go to Assistants
        </Button>
      </div>
    )
  }

  // Determine if assistant is active (has at least one KB and has ragflow_chat_id)
  const isActive = assistant.knowledge_bases.length > 0 && !!assistant.ragflow_chat_id

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/app/assistants">Assistants</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{assistant.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          {/* Bot Icon */}
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Bot className="w-6 h-6 text-blue-600" />
          </div>
          
          {/* Name, Description, Status */}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{assistant.name}</h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(true)}
                className="h-8 w-8 p-0"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            {assistant.description && (
              <p className="text-muted-foreground mt-1">{assistant.description}</p>
            )}
            <Badge 
              variant={isActive ? 'default' : 'secondary'} 
              className={`mt-2 ${isActive ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>

        {/* Right side: Updated time */}
        <div className="flex flex-col items-end gap-1">
          <span className="text-sm text-muted-foreground">
            Updated {formatDistanceToNow(new Date(assistant.updated_at), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="knowledge-bases">Knowledge Bases</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="prompt">Prompt</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="test">Test</TabsTrigger>
          <TabsTrigger value="widget">Widget</TabsTrigger>
          <TabsTrigger value="memory">Memory</TabsTrigger>
        </TabsList>

        <TabsContent value="knowledge-bases">
          <KnowledgeBasesTab 
            assistantId={id!} 
            knowledgeBases={assistant.knowledge_bases} 
          />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab assistantId={id!} assistant={assistant} />
        </TabsContent>

        <TabsContent value="prompt">
          <PromptTab assistantId={id!} assistant={assistant} />
        </TabsContent>

        <TabsContent value="integrations">
          <IntegrationsTab assistantId={id!} />
        </TabsContent>

        <TabsContent value="test">
          <TestTab 
            assistantId={id!} 
            knowledgeBases={assistant.knowledge_bases}
          />
        </TabsContent>

        <TabsContent value="widget">
          <WidgetTab assistantId={id!} assistant={assistant} tenantSlug={tenantSlug} />
        </TabsContent>

        <TabsContent value="memory">
          <MemoryTab assistantId={id!} />
        </TabsContent>
      </Tabs>

      {/* Edit Assistant Modal */}
      <EditAssistantModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        assistantId={id!}
        currentName={assistant.name}
        currentDescription={assistant.description}
      />
    </div>
  )
}
